import React, { useEffect, useState } from 'react';
import useAuth from '../hooks/useAuth';
import { enrichUserPermissions } from '../permissions';
import { useCurrentTramite } from '@/hooks/useCurrentTramite';
import { rolesPermissionsService } from '@/services/rolesPermissionsService';
import { Outlet } from 'react-router-dom';

interface TramiteReadyProps {
  fallback?: React.ReactNode;
  onError?: (error: string) => void;
  children?: React.ReactNode;
}

/**
 * Component that ensures tramite-dependent permissions are loaded before rendering children
 * Useful for views that depend on tramite context and need enriched permissions
 */
export const TramiteReady: React.FC<TramiteReadyProps> = ({
  fallback,
  onError,
  children,
}) => {
  const tramite = useCurrentTramite();
  const { setUser, user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Don't load permissions if user is not ready yet
    if (!user || !user.email || !user.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const loadPermissions = async () => {
      try {
        const roles = await rolesPermissionsService.roles.getAllFromNames(["vendedor", "comprador"]);
        setUser(enrichUserPermissions(user, roles, tramite));
      } catch (err) {
        onError?.(err instanceof Error ? err.message : 'Error loading permissions');
      } finally {
        setLoading(false);
      }
    }
    loadPermissions();
  }, [tramite, user?.id, user?.email]);


  // Show loading state
  if (loading || authLoading) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Cargando...</span>
      </div>
    );
  }


  return children ? <>{children}</> : <Outlet />;
};

export default TramiteReady;
